import { useState } from "react";
import { motion } from "framer-motion";

const ease = [0.22, 1, 0.36, 1];

// Web3Forms access key (public by design — safe to ship client-side).
const ACCESS_KEY = "5cb3dd4b-2b82-40e2-96df-bb7c921f4499";

const Field = ({ id, label, type = "text", textarea, value, onChange, required, autoComplete, caret = false, className = "" }) => (
  <div className={`group ${className}`}>
    <label
      htmlFor={id}
      className="label block mb-3 text-[#8a8a86] transition-colors duration-300 group-focus-within:text-[#ff4f00]"
    >
      {label}
    </label>
    <div className="relative pb-[10px]">
      <div className="relative">
        {textarea ? (
          <textarea
            id={id}
            name={id}
            value={value}
            onChange={onChange}
            required={required}
            placeholder=" "
            rows={2}
            className="block w-full bg-transparent outline-none resize-none caret-[#ff4f00] font-light tracking-[-0.02em] text-xl md:text-[26px] leading-[1.2] text-[#111111]"
          />
        ) : (
          <input
            id={id}
            name={id}
            type={type}
            value={value}
            onChange={onChange}
            required={required}
            placeholder=" "
            autoComplete={autoComplete}
            className="block w-full bg-transparent outline-none caret-[#ff4f00] font-light tracking-[-0.02em] text-xl md:text-[26px] leading-[1.2] text-[#111111]"
          />
        )}
        {caret && (
          <span
            aria-hidden="true"
            className={`field-caret pointer-events-none absolute left-0 w-[2px] md:w-[2.5px] bg-[#ff4f00] text-xl md:text-[26px] ${textarea ? "top-[0.18em] h-[1.05em]" : "top-1/2 -translate-y-1/2 h-[1.05em]"}`}
          />
        )}
      </div>
      <span aria-hidden="true" className="absolute left-0 right-0 bottom-0 h-px bg-[#e5e5e0]" />
      <span
        aria-hidden="true"
        className="absolute left-0 right-0 bottom-0 h-[2px] bg-[#ff4f00] origin-left scale-x-0 transition-transform duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] group-focus-within:scale-x-100"
      />
    </div>
  </div>
);

const Sent = () => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} data-testid="contact-success">
    <span className="relative block w-fit">
      <motion.span
        className="block font-light tracking-[-0.04em] leading-[0.98] text-[clamp(2.6rem,7vw,4.8rem)]"
        initial={{ clipPath: "inset(-0.2em 100% -0.2em 0)" }}
        animate={{ clipPath: "inset(-0.2em 0% -0.2em 0)" }}
        transition={{ duration: 0.9, ease: [0.65, 0, 0.25, 1] }}
      >
        Brief <span className="accent">received.</span>
      </motion.span>
      <motion.span
        aria-hidden="true"
        className="absolute top-[0.04em] bottom-[0.04em] w-[3px] md:w-[5px] bg-[#ff4f00]"
        initial={{ left: "0%", opacity: 0 }}
        animate={{ left: "100%", opacity: [0, 1, 1, 0] }}
        transition={{
          left: { duration: 0.9, ease: [0.65, 0, 0.25, 1] },
          opacity: { duration: 1.05, times: [0, 0.08, 0.84, 1] },
        }}
      />
    </span>
    <motion.p
      className="mt-6 md:mt-8 max-w-md text-sm md:text-base text-[#555555] leading-relaxed"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.5, ease }}
    >
      The director has it, not an auto-responder. Expect a considered reply within one working day.
    </motion.p>
  </motion.div>
);

export const ContactForm = () => {
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [form, setForm] = useState({ name: "", email: "", development: "", message: "", botcheck: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: ACCESS_KEY,
          subject: `New enquiry: ${form.development || form.name || "Frontage"}`,
          from_name: "Frontage website",
          name: form.name,
          email: form.email,
          development: form.development,
          message: form.message,
          botcheck: form.botcheck,
        }),
      });
      const data = await res.json();
      setStatus(data.success ? "success" : "error");
    } catch (err) {
      setStatus("error");
    }
  };

  if (status === "success") return <Sent />;

  const sending = status === "sending";

  return (
    <form onSubmit={submit} data-testid="contact-form" className="max-w-4xl">
      <input
        type="text"
        name="botcheck"
        value={form.botcheck}
        onChange={set("botcheck")}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8 md:gap-y-10">
        <Field id="name" label="Your name" value={form.name} onChange={set("name")} required autoComplete="name" caret />
        <Field id="email" label="Email" type="email" value={form.email} onChange={set("email")} required autoComplete="email" />
        <Field id="development" label="Development and location" value={form.development} onChange={set("development")} className="md:col-span-2" />
        <Field id="message" label="What are you launching?" textarea value={form.message} onChange={set("message")} required className="md:col-span-2" />
      </div>

      <div className="mt-10 md:mt-12 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10">
        <button
          type="submit"
          disabled={sending}
          className="inline-flex items-center gap-4 bg-[#111111] text-[#f9f9f7] px-8 py-4 label tracking-[0.24em] transition-colors duration-300 hover:bg-[#ff4f00] disabled:opacity-60 disabled:hover:bg-[#111111] w-fit"
          data-testid="contact-submit"
        >
          {sending ? "Sending…" : (<>Send the brief <span aria-hidden="true">&rarr;</span></>)}
        </button>
        <p className="text-sm text-[#8a8a86]">
          Prefer to write it yourself?{" "}
          <a href="mailto:hello@frontagedigital.com.au" className="text-[#555555] link-underline">hello@frontagedigital.com.au</a>
        </p>
      </div>

      <div aria-live="polite" className="min-h-[1.25rem] mt-5">
        {status === "error" && (
          <p className="text-sm text-[#555555]" data-testid="contact-error">
            That didn&rsquo;t send. Try again, or email{" "}
            <a href="mailto:hello@frontagedigital.com.au" className="accent link-underline">hello@frontagedigital.com.au</a>{" "}directly.
          </p>
        )}
      </div>
    </form>
  );
};
